import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { vehicleBrandApi } from "../../../modules/vehicle/brand/api";
import { VehicleMappingType } from "../../../modules/vehicle/brand/enums";
import {
  CreateVehicleMapping,
  UpdateVehicleMappingList,
  VehicleMappingFilterPageRequest,
  VehicleMappingTypesRequest,
} from "../../../modules/vehicle/brand/types";
import { EntityObject } from "../../types";

export const VEHICLE_BRAND_QUERY_KEYS = {
  getVehicleMappings: (payload?: VehicleMappingTypesRequest) =>
    payload ? ["getVehicleMappings", payload] : ["getVehicleMappings"],
  filterVehicleBrands: (payload?: VehicleMappingFilterPageRequest) =>
    payload ? ["filterVehicleBrands", payload] : ["filterVehicleBrands"],
  filterVehicleModels: (payload?: EntityObject<VehicleMappingFilterPageRequest>) =>
    payload ? ["filterVehicleModels", payload] : ["filterVehicleModels"],
  remapVehicleMappings: () => ["remapVehicleMappings"],
  mapVehicleMappingsUpdates: () => ["mapVehicleMappingsUpdates"],
  importVehicleMappings: () => ["importVehicleMappings"],
  exportVehicleMappings: () => ["exportVehicleMappings"],
  createVehicleBrandMapping: () => ["createVehicleBrandMapping"],
  updateVehicleBrandMappings: () => ["updateVehicleBrandMappings"],
  createVehicleModelMapping: () => ["createVehicleModelMapping"],
  updateVehicleModelMappings: () => ["updateVehicleModelMappings"],
};

export const useGetVehicleMappingsByType = (types: VehicleMappingType[], enabled?: boolean) => {
  return useQueries({
    queries: types.map((type) => ({
      queryKey: VEHICLE_BRAND_QUERY_KEYS.getVehicleMappings({ queriedMappings: [type] }),
      queryFn: async () => {
        const { data } = await vehicleBrandApi.getVehicleMappings({ queriedMappings: [type] });

        return data;
      },
      enabled,
    })),
    combine: (results) => ({
      data: results.flatMap((r) => r.data ?? []),
      isLoading: results.some((r) => r.isLoading),
      isFetching: results.some((r) => r.isFetching),
    }),
  });
};

export const useFilterVehicleBrands = (payload: VehicleMappingFilterPageRequest) => {
  return useQuery({
    queryKey: VEHICLE_BRAND_QUERY_KEYS.filterVehicleBrands(payload),
    queryFn: async () => {
      const { data } = await vehicleBrandApi.filterVehicleBrands(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useFilterVehicleModels = (payload: EntityObject<VehicleMappingFilterPageRequest>, enabled?: boolean) => {
  return useQuery({
    queryKey: VEHICLE_BRAND_QUERY_KEYS.filterVehicleModels(payload),
    queryFn: async () => {
      const { data } = await vehicleBrandApi.filterVehicleModels(payload);

      return data;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useRemapVehicleMappings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.remapVehicleMappings(),
    mutationFn: (payload: VehicleMappingTypesRequest) => {
      return vehicleBrandApi.remapVehicleMappings(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      mutatedPayload.queriedMappings?.forEach((type) => {
        queryClient.invalidateQueries({
          queryKey: VEHICLE_BRAND_QUERY_KEYS.getVehicleMappings({ queriedMappings: [type] }),
        });
      });
    },
  });
};

export const useMapVehicleMappingsUpdates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.mapVehicleMappingsUpdates(),
    mutationFn: async (payload: VehicleMappingTypesRequest) => {
      await vehicleBrandApi.mapVehicleMappingsUpdates(payload);
      await vehicleBrandApi.unmapObsoleteVehicleMappings(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      mutatedPayload.queriedMappings?.forEach((type) => {
        queryClient.invalidateQueries({
          queryKey: VEHICLE_BRAND_QUERY_KEYS.getVehicleMappings({ queriedMappings: [type] }),
        });
      });
    },
  });
};

export const useImportVehicleMappings = () => {
  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.importVehicleMappings(),
    mutationFn: (payload: FormData) => {
      return vehicleBrandApi.importVehicleMappings(payload);
    },
  });
};

export const useExportVehicleMappings = () => {
  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.exportVehicleMappings(),
    mutationFn: async () => {
      const response = await vehicleBrandApi.exportVehicleMappings();
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useCreateVehicleBrandMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.createVehicleBrandMapping(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateVehicleMapping) => {
      const { data } = await vehicleBrandApi.createVehicleBrandMapping(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_BRAND_QUERY_KEYS.filterVehicleBrands() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateVehicleBrandMappings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.updateVehicleBrandMappings(),
    mutationFn: async (payload: UpdateVehicleMappingList) => {
      const { data } = await vehicleBrandApi.updateVehicleBrandMappings(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_BRAND_QUERY_KEYS.filterVehicleBrands() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useCreateVehicleModelMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.createVehicleModelMapping(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateVehicleMapping>) => {
      const { data } = await vehicleBrandApi.createVehicleModelMapping(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_BRAND_QUERY_KEYS.filterVehicleModels() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateVehicleModelMappings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_BRAND_QUERY_KEYS.updateVehicleModelMappings(),
    mutationFn: async (payload: EntityObject<UpdateVehicleMappingList>) => {
      const { data } = await vehicleBrandApi.updateVehicleModelMappings(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VEHICLE_BRAND_QUERY_KEYS.filterVehicleModels() });
      Notification.itemUpdatedNotification();
    },
  });
};
